import React, { useState } from "react";
import { API } from "@/lib/api";
import { Cpu, FileDown, Loader2, BellRing, Satellite } from "lucide-react";
import { toast } from "sonner";

export const ScanToolbar = ({ scan }) => {
  const [busy, setBusy] = useState(false);
  const q = scan.result?.quantum || {};
  const scene = scan.result?.scene;
  const alert = scan.alert;

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/scans/${scan.id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("PDF export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `QuantumSpectra_scan_${scan.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="scan-toolbar">
      <div className="flex flex-wrap items-center gap-3">
        <div className={`flex items-center gap-2 px-3 py-1.5 border text-xs font-mono ${q.quantum ? "border-[#8A2BE2]/60 bg-[#8A2BE2]/10 text-[#8A2BE2]" : "border-white/10 text-slate-400"}`} data-testid="quantum-badge">
          <Cpu className="h-3.5 w-3.5" />
          <span>{q.backend || scan.backend}</span>
          {q.algorithm && <span className="text-slate-500">· {q.algorithm}</span>}
          {q.confidence != null && <span>· conf {q.confidence}</span>}
          {q.kernel && <span className="text-slate-500">· K+ {q.kernel.positive} / K− {q.kernel.negative}</span>}
          {q.fallback_reason && <span className="text-[#F59E0B]">· fallback: {q.fallback_reason}</span>}
        </div>
        {scene && (
          <div className="flex items-center gap-2 px-3 py-1.5 border border-[#00FFFF]/40 text-[#00FFFF] text-xs font-mono" data-testid="scene-badge">
            <Satellite className="h-3.5 w-3.5" /> {scene.id} <span className="text-slate-500">· cloud {scene.cloud_cover}%</span>
            {scan.result?.ndvi_real && <span className="text-[#00FF7F]" data-testid="real-ndvi-badge">· REAL {(scan.result.indices_real?.bands || ["B04", "B08"]).join("/")} · {Object.keys(scan.result.indices_real?.indices || { ndvi: 1 }).join("/").toUpperCase()} · {scan.result.ndvi_real.pixels}px · veg {Math.round(scan.result.ndvi_real.vegetated_fraction * 100)}%</span>}
          </div>
        )}
        <button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={busy}
          className="ml-auto flex items-center gap-2 border border-white/15 px-3 py-1.5 text-xs hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Download PDF
        </button>
      </div>
      {alert && (
        <div className="flex flex-wrap items-center gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-3 py-2 text-xs" data-testid="alert-status">
          <BellRing className="h-3.5 w-3.5 text-[#F59E0B]" />
          <span className="text-[#F59E0B]">Contamination alert sent</span>
          <span className="text-slate-400">· {alert.exceedances} MRL exceedance(s) · {alert.delivered}/{alert.recipients.length} delivered</span>
          <span className="flex flex-wrap gap-1.5 ml-1">
            {alert.recipients.map((r) => (
              <span key={r.email} title={r.reason || ""} className={`font-mono px-1.5 py-0.5 border ${r.status === "delivered" ? "border-[#10B981]/40 text-[#10B981]" : "border-white/10 text-slate-500 line-through"}`}>{r.email}</span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};
